/**
 * Utility functions for interacting with Usercentrics CMP
 * Handles both v1 and v2/v3 APIs
 */

/**
 * Check if Usercentrics has been loaded on the page
 * @returns {boolean} True if any version of Usercentrics is loaded
 */
export const isUsercentricsLoaded = () => {
  return !!(
    (window.UC_UI && typeof window.UC_UI.getServicesBaseInfo === 'function') || // v2/v3
    (window.usercentrics && typeof window.usercentrics.getConsents === 'function') // v1
  );
};

/**
 * Gets consent status for all service categories from Usercentrics
 * @returns {Object} Object with functional, analytics, marketing consent statuses and loaded flag
 */
export const getConsentStatus = () => {
  // Default state - assume no consent until confirmed
  const result = {
    functional: false,
    analytics: false,
    marketing: false,
    loaded: false
  };

  try {
    // No auto-consent for localhost since we're testing in dev environment
    // Keep this commented code for reference
    /*
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('[Consent] Development environment detected, auto-accepting all consent categories');
      return {
        functional: true,
        analytics: true,
        marketing: true,
        loaded: true
      };
    }
    */
    
    let usercentricsDetected = false;
    
    // Try Usercentrics v2/v3 API first
    if (window.UC_UI && window.UC_UI.getServicesBaseInfo) {
      usercentricsDetected = true;
      const services = window.UC_UI.getServicesBaseInfo();
      
      result.functional = services.some(service => 
        service.categorySlug === 'functional' && 
        service.consent.status === true
      );
      
      result.analytics = services.some(service => 
        service.categorySlug === 'analytics' && 
        service.consent.status === true
      );
      
      result.marketing = services.some(service => 
        service.categorySlug === 'marketing' && 
        service.consent.status === true
      );
    } 
    // Try Usercentrics v1 API as fallback
    else if (window.usercentrics && window.usercentrics.getConsents) {
      usercentricsDetected = true;
      const consents = window.usercentrics.getConsents();
      
      result.functional = consents.some(consent => 
        consent.categorySlug === 'functional' && 
        consent.consentStatus === true
      );
      
      result.analytics = consents.some(consent => 
        consent.categorySlug === 'analytics' && 
        consent.consentStatus === true
      );
      
      result.marketing = consents.some(consent => 
        consent.categorySlug === 'marketing' && 
        consent.consentStatus === true
      );
    }
    
    result.loaded = usercentricsDetected;
    
  } catch (error) {
    console.error('[Consent] Error checking consent status:', error);
  }
  
  return result;
};

/**
 * Gets consent status for a specific service by ID
 * @param {string} serviceId - The Usercentrics service ID to check
 * @returns {boolean|null} True if consented, false if denied, null if not loaded or error
 */
export const getServiceConsentStatus = (serviceId) => {
  if (!serviceId) return null;
  
  try {
    // If on localhost, auto-consent for development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return true;
    }
    
    // Try Usercentrics v2/v3 API first
    if (window.UC_UI && window.UC_UI.getServicesBaseInfo) {
      const services = window.UC_UI.getServicesBaseInfo();
      const service = services.find(s => s.id === serviceId);
      
      if (service) {
        return service.consent.status === true;
      }
    } 
    // Try Usercentrics v1 API as fallback
    else if (window.usercentrics && window.usercentrics.getConsents) {
      const consents = window.usercentrics.getConsents();
      const service = consents.find(s => s.templateId === serviceId);
      
      if (service) {
        return service.consentStatus === true;
      }
    }
    
    return null;
  } catch (error) {
    console.error(`[Consent] Error checking consent for service ${serviceId}:`, error);
    return null;
  }
};

/**
 * Opens the Usercentrics consent preferences dialog (Second Layer)
 */
export const openConsentPreferences = () => {
  try {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('[Consent] Development environment detected, simulating consent dialog');
      // For development, just show the confirm but don't use the result
      window.confirm('Development mode: Accept all cookies?');
      return;
    }
    
    // Try Usercentrics v2/v3 API first
    if (window.UC_UI && window.UC_UI.showSecondLayer) {
      window.UC_UI.showSecondLayer();
      return;
    } 
    // Try Usercentrics v1 API
    else if (window.usercentrics && window.usercentrics.showSecondLayer) {
      window.usercentrics.showSecondLayer();
      return;
    }
    
    console.warn('[Consent] Could not open consent preferences - Usercentrics not loaded');
    alert('Cookie preferences are not available right now.');
  } catch (error) {
    console.error('[Consent] Error opening consent preferences:', error);
  }
};

/**
 * Opens the Usercentrics first layer (initial consent banner)
 */
export const openConsentBanner = () => {
  try {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('[Consent] Development environment detected, simulating consent banner');
      // For development, just show the confirm but don't use the result
      window.confirm('Development mode: Accept all cookies?');
    }
    
    // Try Usercentrics v2/v3 API first
    if (window.UC_UI && window.UC_UI.showFirstLayer) {
      window.UC_UI.showFirstLayer();
      return true;
    } 
    // Try Usercentrics v1 API as fallback
    else if (window.usercentrics && window.usercentrics.showFirstLayer) {
      window.usercentrics.showFirstLayer();
      return true;
    }
    
    // No Usercentrics detected or API call failed
    return false;
  } catch (error) {
    console.error('[Consent] Error opening consent banner:', error);
    return false;
  }
};

/**
 * Adds a listener for Usercentrics consent changes
 * This is critical for real-time handling of cookie consent changes
 * 
 * @param {Function} callback - Callback function that will receive the updated consent status
 * @returns {Function|null} Function to remove the listener, or null if failed
 */
export const addConsentChangedListener = (callback) => {
  if (!callback || typeof callback !== 'function') {
    console.error('[Consent] Invalid callback provided for consent listener');
    return null;
  }

  try {
    const timestamp = new Date().toISOString();
    console.log(`[Consent DEBUG][${timestamp}] Setting up consent changed listener...`);
    console.log(`[Consent DEBUG][${timestamp}] Usercentrics is loaded:`, isUsercentricsLoaded());
    
    // Display current consent status at setup time
    const initialStatus = getConsentStatus();
    console.log(`[Consent DEBUG][${timestamp}] Initial consent status at listener setup:`, initialStatus);
    
    // Track registered event listeners for cleanup
    const registeredListeners = [];
    
    // Helper to register event listeners and track them
    const registerListener = (eventName, handler) => {
      console.log(`[Consent DEBUG][${timestamp}] Registering listener for event: ${eventName}`);
      window.addEventListener(eventName, handler);
      registeredListeners.push({ eventName, handler });
    };
    
    // Enhanced callback handler with debug logging
    const createHandler = (source) => {
      return (event) => {
        const eventTimestamp = new Date().toISOString();
        console.log(`[Consent DEBUG][${eventTimestamp}] 🔔 CONSENT EVENT DETECTED from: ${source}`);
        console.log(`[Consent DEBUG][${eventTimestamp}] Event details:`, event);
        
        // Get updated status with a small delay to ensure Usercentrics has updated internal state
        setTimeout(() => {
          const updatedStatus = getConsentStatus();
          console.log(`[Consent DEBUG][${eventTimestamp}] Updated consent status after event:`, updatedStatus);
          
          // Only invoke callback if we actually have a status object
          if (updatedStatus) {
            console.log(`[Consent DEBUG][${eventTimestamp}] Calling consent change callback with status`); 
            callback(updatedStatus);
          } else {
            console.error(`[Consent DEBUG][${eventTimestamp}] Failed to get updated consent status after event`);
          }
        }, 50); // Small delay to ensure Usercentrics has updated its internal state
      };
    };

    // For Usercentrics v2/v3
    if (window.UC_UI) {
      console.log('[Consent DEBUG] Usercentrics UI API detected (v2/v3)');
      
      // Create handlers with source info
      const uiInitHandler = createHandler('UC_UI_INITIALIZED');
      const uiEventHandler = createHandler('UC_UI_CMP_EVENT');
      const consentUpdateHandler = createHandler('UC_UI_CONSENT_UPDATE');
      
      // Register all relevant events
      registerListener('UC_UI_INITIALIZED', uiInitHandler);
      registerListener('UC_UI_CMP_EVENT', uiEventHandler);
      registerListener('UC_UI_CONSENT_UPDATE', consentUpdateHandler);
      
      // Add direct API listener for the Smart Data Protector version
      if (window.UC_UI.isInitialized && typeof window.UC_UI.onConsentUpdated === 'function') {
        console.log('[Consent DEBUG] Using UC_UI.onConsentUpdated API');
        window.UC_UI.onConsentUpdated(createHandler('UC_UI.onConsentUpdated'));
      }
    }
    // For Usercentrics v1 (older version)
    else if (window.usercentrics) {
      console.log('[Consent DEBUG] Legacy Usercentrics API detected (v1)');
      
      // Create handlers with source info
      const consentHandler = createHandler('ucApiConsent');
      const initHandler = createHandler('ucApiInitialized');
      
      // Register legacy events
      registerListener('ucApiConsent', consentHandler);
      registerListener('ucApiInitialized', initHandler);
      
      // Set up direct callback if available
      if (typeof window.usercentrics.onConsentUpdate === 'function') {
        // console.log('[Consent DEBUG] Using legacy usercentrics.onConsentUpdate API');
        window.usercentrics.onConsentUpdate(createHandler('usercentrics.onConsentUpdate'));
      }
    } else {
      // No Usercentrics detected
      console.warn('[Consent DEBUG] Usercentrics not detected, consent changes will not be monitored');
      return null;
    }
    
    // Explicitly check status now and trigger callback to ensure proper initialization
    setTimeout(() => {
      const initTimestamp = new Date().toISOString();
      const initialStatus = getConsentStatus();
      if (initialStatus) {
        console.log(`[Consent DEBUG][${initTimestamp}] Fallback initial consent status check:`, initialStatus);
        console.log(`[Consent DEBUG][${initTimestamp}] Triggering initial callback with detected status`);
        callback(initialStatus);
      } else {
        console.warn(`[Consent DEBUG][${initTimestamp}] Fallback check failed to get consent status`);
      }
      
      // Verify Usercentrics API availability
      console.log(`[Consent DEBUG][${initTimestamp}] Usercentrics API availability check:`, {
        'UC_UI': !!window.UC_UI,
        'UC_UI.isInitialized': window.UC_UI?.isInitialized,
        'usercentrics': !!window.usercentrics,
        'document.cookie': document.cookie
      });
    }, 500);
    
    // Return cleanup function
    return () => {
      const cleanupTimestamp = new Date().toISOString();
      console.log(`[Consent DEBUG][${cleanupTimestamp}] Removing ${registeredListeners.length} consent change listeners`);
      registeredListeners.forEach(({ eventName, handler }) => {
        try {
          window.removeEventListener(eventName, handler);
          console.log(`[Consent DEBUG][${cleanupTimestamp}] Successfully removed listener for ${eventName}`);
        } catch (err) {
          console.error(`[Consent DEBUG][${cleanupTimestamp}] Error removing event listener for ${eventName}:`, err);
        }
      });
    };
  } catch (error) {
    console.error('[Consent] Error setting up consent change listener:', error);
    return null;
  }
};
